import { useEffect, useState } from "react";
import { Link, Navigate, Outlet } from "react-router-dom";
import { BookOpen, LogOut, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Sidebar } from "@/components/layout/Sidebar";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const RconIndicator = ({ communityId }) => {
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    const load = () => api.get(`/communities/${communityId}/rcon/status`).then((r) => live && setState(r.data.state)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    const onChange = () => setTimeout(load, 600);
    window.addEventListener("ranger:rcon", onChange);
    return () => { live = false; clearInterval(t); window.removeEventListener("ranger:rcon", onChange); };
  }, [communityId]);
  return state ? <StatusBadge state={state} label={`RCON ${state === "unconfigured" ? "not configured" : state}`} testId="header-rcon-status" /> : null;
};

const UserChip = () => {
  const { user, active, logout } = useAuth();
  return (
    <div className="flex items-center gap-3">
      {user.avatar ? <img src={user.avatar} alt="" className="h-8 w-8 rounded-sm" /> : (
        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#111A2E] font-display font-bold text-[#E5202E]">{user.name[0]}</div>
      )}
      <div className="hidden leading-tight sm:block">
        <div data-testid="header-user-name" className="text-sm text-slate-200">{user.name}</div>
        <div data-testid="header-user-role" className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{user.is_owner ? "owner" : active?.role}</div>
      </div>
      <button data-testid="logout-button" onClick={logout} aria-label="Sign out" className="rounded-sm border border-[#1E2D4A] p-2 text-slate-400 hover:border-red-500/40 hover:text-red-400">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
};

export default function AppShell() {
  const { session, active } = useAuth();
  const [open, setOpen] = useState(false);
  if (session === null) return <div className="flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-widest text-slate-500">Authenticating…</div>;
  if (session === false) return <Navigate to="/login" replace />;
  if (!active) return <Navigate to="/portal" replace />;

  return (
    <div className="flex min-h-screen bg-[#050811]">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-[#1E2D4A] lg:block"><Sidebar /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-[#1E2D4A] bg-[#070A14] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-[#1E2D4A] bg-[#070A14]/90 px-4 py-3 backdrop-blur-md sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} aria-label="Open menu" className="rounded-sm border border-[#1E2D4A] p-2 text-slate-300 lg:hidden"><Menu className="h-4 w-4" /></button>
            <div className="min-w-0">
              <div data-testid="header-community-name" className="truncate font-display text-lg font-bold uppercase tracking-wider text-slate-100">{active?.name || "No community access"}</div>
            </div>
            {active && <div className="hidden md:block"><RconIndicator communityId={active.id} /></div>}
          </div>
          <div className="flex items-center gap-3"><Link data-testid="header-docs-link" to="/docs" className="flex items-center gap-1.5 rounded-sm border border-[#1E2D4A] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-300 transition-colors hover:border-[#E5202E]/50 hover:text-white"><BookOpen className="h-3.5 w-3.5" /><span className="hidden sm:inline">Docs</span></Link><NotificationBell /><UserChip /></div>
        </header>
        <main className="rg-grid-bg flex-1 px-4 py-8 sm:px-8">
          <div className="mx-auto max-w-7xl">{active ? <Outlet /> : <p className="text-slate-400">Your account has no community access.</p>}</div>
        </main>
      </div>
    </div>
  );
}
